/**
 * Player-facing release notes, shown on the character-select screen.
 *
 * Newest release first. Add an entry here whenever a build ships something
 * players can actually notice - keep the wording plainer than a commit message
 * and lead with what it means for the player, not how it was built.
 */
export interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  changes: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.16.1',
    date: '8 Oct 2026',
    title: 'Fixes',
    changes: [
      'Removed the frosted-glass blur behind the HUD panels. It forced Firefox to recompute a blur over the moving world for every panel, every frame - the real reason its frame rate lagged. Panels are a little more opaque so they read just as well without it.',
    ],
  },
  {
    version: '0.16.0',
    date: '8 Oct 2026',
    title: 'Smoother on Firefox',
    changes: [
      'Firefox now skips the most expensive layers for its slower canvas: the terrain grain, the additive glow on shots and spell effects, and the outlines on damage numbers. Everything you need to read still draws.',
      'Slimmed the frame for everyone: no wasted full-screen clear where the ground covers it, the vignette is a pre-baked image instead of a per-frame gradient, and the minimap repaints ten times a second.',
      'The game now adapts its resolution when frames run long and sharpens back up when there is headroom. The percentage shows in the perf panel (F) next to quality.',
    ],
  },
  {
    version: '0.15.0',
    date: '8 Oct 2026',
    title: 'Party chat',
    changes: [
      'Type /p (or /party) before a message to talk to your party only. Party chat shows in green and never reaches the rest of the server.',
      'It costs the same as any message and follows the same cooldown, so a group can plan without spamming.',
    ],
  },
  {
    version: '0.14.0',
    date: '8 Oct 2026',
    title: 'Hunts that point the way',
    changes: [
      'The bounty board now offers hunts that lead to landmarks: slay the named boss that lairs there (the Grave-Wight, Ruin Colossus and the rest).',
      'A gold arrow shows the way to the target landmark with its name and distance, and a matching waypoint appears on the minimap.',
      'Boss hunts appear once a lair suited to your level is within reach, so they start around the mid-game.',
    ],
  },
  {
    version: '0.13.0',
    date: '8 Oct 2026',
    title: 'Signature boss loot',
    changes: [
      "Each world boss now guards an item unique to its lair: the Grave-Wight's Shroud, the Colossus Crown, the Warden's Treads, the Chieftain's Striders and the Fallen Priest's Mitre.",
      'They are epic pieces tied to a gear set, with a bonus no ordinary drop can roll, and each one drops every time its boss falls.',
      'Their affixes still roll, so a perfect copy is a hunt of its own - the deep map finally has something specific to chase.',
    ],
  },
  {
    version: '0.12.0',
    date: '8 Oct 2026',
    title: 'Money matters now',
    changes: [
      'Coins are much harder to come by - monsters drop far less, so you have to work for a fortune instead of tripping over one.',
      'The smiths now reforge gear: spend coins to push a piece up one rarity, adding a new bonus each step. Weapons go to Brynja, armour to Havard.',
      'Potions and scrolls get more expensive as you level, so they stay a real cost rather than pocket change.',
    ],
  },
  {
    version: '0.11.0',
    date: '8 Oct 2026',
    title: 'A proper bestiary',
    changes: [
      'Eight new creatures fill out the long climb: wolves, skeletons, cultists, golems, harpies, ogres, wraiths and dragonkin.',
      'They appear in bands as you head outward, so what you fight at level 70 looks nothing like the slimes and boars around the city.',
      'The old favourites still hold the early and middle game, and every newcomer is a melee or ranged foe you already know how to read.',
    ],
  },
  {
    version: '0.10.1',
    date: '8 Oct 2026',
    title: 'Fixes',
    changes: [
      'Accepting a bounty no longer announces it twice.',
      'Selling gear works at every vendor again, not just the two smiths - Marrow the Fence will buy your spares too.',
    ],
  },
  {
    version: '0.10.0',
    date: '2 Oct 2026',
    title: 'Delete characters and send feedback',
    changes: [
      'You can delete a character from the pick screen now - press the little x on its card. It asks you to confirm first, so there are no accidents.',
      'Added a feedback form on the login screen for bugs, balance and ideas. It holds up to 500 characters and lands in a log on the server for us to read.',
      'All characters were reset to go with the new 1-100 experience curve, so everyone begins a fresh climb.',
    ],
  },
  {
    version: '0.9.0',
    date: '2 Oct 2026',
    title: 'Bounties that follow your level',
    changes: [
      'Bounties are no longer a fixed list - they are built from your level, so there is always work whatever your rank. A level-50 character is sent to kill level 48-53 monsters.',
      'Progress only counts kills inside your band, so farming low-level country does nothing. The bounty board quietly points you further out.',
      'Two jobs name a specific monster that lives at your level; a third takes any monster in the band for a larger count.',
      'A finished bounty pays coins plus a modest experience bonus on top of the kills - enough to matter, not enough to skip the climb.',
    ],
  },
  {
    version: '0.8.0',
    date: '2 Oct 2026',
    title: 'Elites and a proper marketplace',
    changes: [
      'Elite monsters now prowl the wild: gold-auraed, heavily buffed versions of ordinary creatures with triple health and triple experience. They always drop a piece of gear, usually rare or better - worth stopping for.',
      'The city now has three vendors instead of one. Marrow the Fence still sells potions and town scrolls; Brynja the Bladesmith deals in weapons; Havard the Armorer fits you out in armour.',
      'The smiths keep level-scaled stock that turns over, and each of them will buy spare gear back from you.',
    ],
  },
  {
    version: '0.7.0',
    date: '2 Oct 2026',
    title: 'The long climb to level 100',
    changes: [
      'The level cap is now 100, and every level costs more than the last. This is an old-school grind measured in tens of thousands of kills, not an afternoon.',
      'Experience is gated by level: monsters eight or more levels below you give none at all, and fighting above your level pays a bonus. Pushing into dangerous country is now the only way up.',
      'To match, the wilderness reaches further - monsters climb to around level 95 at the far corners of the map, so there is somewhere left to grow into.',
    ],
  },
  {
    version: '0.6.0',
    date: '2 Oct 2026',
    title: 'World bosses',
    changes: [
      'A named boss now lairs at certain landmarks: the Grave-Wight, Ruin Colossus, Tower Warden, Bandit Chieftain and Fallen Priest.',
      'Walk close and it wakes. It is far tougher than the surrounding wildlife and always drops two pieces of rare-or-better gear plus a pile of coins.',
      'A slain boss stays gone for a good while, so a kill is an event rather than a farm. The whole server is told when one rises and when it falls.',
    ],
  },
  {
    version: '0.5.0',
    date: '2 Oct 2026',
    title: 'Set bonuses',
    changes: [
      'Some gear now belongs to a set: Boarhide, Wispweave, Berserker and Wayfarer.',
      'Wear two pieces for a small bonus and three for a bigger one. Tooltips show what each set grants and how many pieces you have - including what equipping the piece in hand would bring you to.',
      'Set bonuses scale with the weakest piece in the set, so a set is only as strong as the item you least want to replace.',
    ],
  },
  {
    version: '0.4.2',
    date: '2 Oct 2026',
    title: 'Compare gear at a glance',
    changes: [
      'Hovering a piece of gear now shows it beside what you are already wearing, stat by stat.',
      'Improvements glow green and downgrades glow red, so you can tell at a glance whether a drop is an upgrade.',
      'The same comparison appears on the trader\'s stock, so you can see if something is worth buying first.',
    ],
  },
  {
    version: '0.4.1',
    date: '2 Oct 2026',
    title: 'The trader deals in gear',
    changes: [
      'Marrow the Fence keeps a rotating stock of four pieces of gear, rolled for your level. Stand next to him and press E to browse.',
      'His goods are uncommon or better, and the stock turns over every few minutes so it is worth checking back.',
    ],
  },
  {
    version: '0.4.0',
    date: '2 Oct 2026',
    title: 'Equipment',
    changes: [
      'Monsters can drop gear now - weapons, helms, vests, boots and charms - in four rarities with random bonuses.',
      'Every drop is its own item: a "Superior Sword of Fury" is not the same as a plain sword, and its stats are shown on hover.',
      'Press I for the new Equipment screen. Click a piece in your pack to wear it, or a worn piece to take it off.',
      'Weapons are class-locked - only warriors wield swords, rangers bows and mages staves. Armour fits everyone.',
      'Sell spare gear to Marrow the Fence for coins.',
    ],
  },
  {
    version: '0.3.0',
    date: '2 Oct 2026',
    title: 'Biomes',
    changes: [
      'The world has regions now: green plains, dark deepwoods, autumn woods, dry dustlands, fetid mires, grey highlands and pale frostlands.',
      'Regions blend into one another as you travel, and the whole map is coloured on the minimap so you can see where each one lies.',
      'The badge under the minimap now names the region you are standing in instead of just saying "wilderness".',
      'The country around the city is always plains, so new characters start somewhere familiar.',
    ],
  },
  {
    version: '0.2.0',
    date: '2 Oct 2026',
    title: 'Wilderness landmarks',
    changes: [
      'The wild is no longer just rocks and trees: scout out bandit camps, hamlets, graveyards, chapels, ruins, shrines, farmsteads and watchtowers hidden across the map.',
      'Landmarks show up as bright marks on the minimap once you get close enough to see them.',
      'Every landmark is a den - monsters that lair there are themed to the place, so graveyards crawl with wraiths and camps swarm with raiders.',
      'Shrines and chapels are places of rest. Linger in their grounds to recover health and mana, unless something has just taken a bite out of you.',
    ],
  },
  {
    version: '0.1.1',
    date: '2 Oct 2026',
    title: 'Fixes',
    changes: [
      'Switching characters no longer leaves you with a level-1 health pool - you return at full health for your actual level.',
    ],
  },
];
